import type {
  ContextualMenuGroup,
  ContextualMenuItemWithEmoji,
  ContextualMenuItemWithIcon,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {
  ContextualMenuGroupType,
  ContextualMenuItemGraphicType,
} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu/ai-prompt-box-contextual-menu.types';
import {getSessionStorageItem, setSessionStorageItem} from '@Utils/session-storage.util';

const LENGTH_GROUP_KEY = 'length';
const TONE_GROUP_KEY = 'tone';

export const getLengthAndToneContextualMenuGroups = (): ContextualMenuGroup[] => {
  return [
    {
      label: window.i18next.t('pmwjs_length'),
      key: LENGTH_GROUP_KEY,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {
          label: window.i18next.t('pmwjs_short'),
          value: 'short',
          active: true,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-short-text',
        } as ContextualMenuItemWithIcon,
        {
          label: window.i18next.t('pmwjs_medium'),
          value: 'medium',
          active: false,
          graphicType: ContextualMenuItemGraphicType.ICON,
          icon: 'icon-align-block',
        } as ContextualMenuItemWithIcon,
        {label: window.i18next.t('pmwjs_long'), value: 'long', active: false, graphicType: ContextualMenuItemGraphicType.ICON, icon: 'icon-bars'} as ContextualMenuItemWithIcon,
      ],
    },
    {
      label: window.i18next.t('pmwjs_tone'),
      key: TONE_GROUP_KEY,
      type: ContextualMenuGroupType.PILLS,
      items: [
        {label: window.i18next.t('pmwjs_neutral'), value: 'neutral', active: true, graphicType: ContextualMenuItemGraphicType.EMOJI, emoji: '📘'} as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_professional'),
          value: 'professional',
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💼',
        } as ContextualMenuItemWithEmoji,
        {label: window.i18next.t('pmwjs_excited'), value: 'excited', active: false, graphicType: ContextualMenuItemGraphicType.EMOJI, emoji: '⚡'} as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_confident'),
          value: 'confident',
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '🦁',
        } as ContextualMenuItemWithEmoji,
        {label: window.i18next.t('pmwjs_witty'), value: 'witty', active: false, graphicType: ContextualMenuItemGraphicType.EMOJI, emoji: '🧠'} as ContextualMenuItemWithEmoji,
        {
          label: window.i18next.t('pmwjs_empowering'),
          value: 'empowering',
          active: false,
          graphicType: ContextualMenuItemGraphicType.EMOJI,
          emoji: '💪',
        } as ContextualMenuItemWithEmoji,
        {label: window.i18next.t('pmwjs_funny'), value: 'funny', active: false, graphicType: ContextualMenuItemGraphicType.EMOJI, emoji: '🤹'} as ContextualMenuItemWithEmoji,
      ],
    },
  ];
};

interface ReuseOrUseGeneratedContentParams {
  storageKey: string;
  lookupKey: string;
  existingKey?: string;
  reuse: (storedKey: string) => Promise<void>;
  use: () => Promise<string>;
}

export const reuseOrUseGeneratedContent = async ({storageKey, lookupKey, existingKey, reuse, use}: ReuseOrUseGeneratedContentParams): Promise<void> => {
  if (existingKey) {
    await reuse(existingKey);

    return;
  }

  const usedKeys = (getSessionStorageItem(storageKey) as Record<string, string> | null) ?? {};
  const previouslyUsedKey = usedKeys[lookupKey];

  if (previouslyUsedKey !== undefined) {
    await reuse(previouslyUsedKey);

    return;
  }

  const usedKey = await use();
  if (!usedKey) {
    return;
  }

  usedKeys[lookupKey] = usedKey;
  setSessionStorageItem(storageKey, usedKeys);
};
